<template>
  <div class="tg-wrap" ref="wrap">
    <div class="tg-topbar">
      <div class="tg-top-pill">
        <span class="tg-top-icon">🧱</span>
        <span class="tg-top-title">Tetris</span>
      </div>
      <div class="tg-top-stats">
        <div class="tg-top-stat">
          <span class="tg-top-val">{{ score }}</span>
          <span class="tg-top-lbl">score</span>
        </div>
        <div class="tg-top-stat">
          <span class="tg-top-val">{{ lines }}</span>
          <span class="tg-top-lbl">lines</span>
        </div>
        <div class="tg-top-stat tg-level-pill" :style="{ background: levelColor }">
          <span class="tg-top-val tg-lv-val">LV {{ level }}</span>
        </div>
      </div>
    </div>

    <div class="tg-main">
      <!-- board -->
      <div class="tg-board" ref="board">
        <div v-for="(row, r) in displayBoard" :key="r" class="tg-row">
          <div
            v-for="(cell, c) in row"
            :key="c"
            class="tg-cell"
            :style="cellStyle(cell)"
          ></div>
        </div>

        <!-- start overlay -->
        <div v-if="!running && !over" class="tg-overlay">
          <div class="tg-ov-card">
            <div class="tg-ov-emoji">🧱</div>
            <h2 class="tg-ov-title">Tetris</h2>
            <p class="tg-ov-sub">Arrows move · Space = rotate · ↑ = drop</p>
            <button class="tg-btn" @click="startGame">▶ Play Now</button>
          </div>
        </div>

        <!-- game over overlay -->
        <div v-if="over" class="tg-overlay">
          <div class="tg-ov-card">
            <div class="tg-ov-emoji">{{ score >= 500 ? '🏆' : '💔' }}</div>
            <h2 class="tg-ov-title">{{ score >= 500 ? 'Amazing!' : 'Game Over' }}</h2>
            <div class="tg-final-score">{{ score }} <span>pts</span></div>
            <button class="tg-btn" @click="startGame">↺ Play Again</button>
          </div>
        </div>
      </div>

      <!-- sidebar -->
      <div class="tg-side">
        <div class="tg-next-box">
          <div class="tg-side-label">NEXT</div>
          <div class="tg-next">
            <div v-for="(row, r) in nextPreview" :key="r" class="tg-row tg-next-row">
              <div v-for="(cell, c) in row" :key="c" class="tg-cell tg-next-cell" :style="cellStyle(cell)"></div>
            </div>
          </div>
        </div>

        <div class="tg-side-stat">
          <div class="tg-stat-lbl">BEST</div>
          <div class="tg-stat-val">{{ best }}</div>
        </div>
        <div class="tg-side-stat">
          <div class="tg-stat-lbl">LINES</div>
          <div class="tg-stat-val">{{ lines }}</div>
        </div>
      </div>
    </div>

    <!-- controls -->
    <div class="tg-controls">
      <button class="tg-ctrl-btn" @click="moveLeft">◀</button>
      <button class="tg-ctrl-btn" @click="rotate">↻</button>
      <button class="tg-ctrl-btn" @click="softDrop">▼</button>
      <button class="tg-ctrl-btn" @click="moveRight">▶</button>
      <button class="tg-ctrl-btn tg-ctrl-drop" @click="hardDrop">⬇ Drop</button>
    </div>
  </div>
</template>

<script>
const COLS = 10;
const ROWS = 20;
const EMPTY = '';

const PIECES = {
  I: { cells: [[0,1],[1,1],[2,1],[3,1]], color: '#06b6d4', light: '#cffafe' },
  O: { cells: [[0,0],[1,0],[0,1],[1,1]], color: '#f59e0b', light: '#fef3c7' },
  T: { cells: [[1,0],[0,1],[1,1],[2,1]], color: '#a855f7', light: '#f3e8ff' },
  S: { cells: [[1,0],[2,0],[0,1],[1,1]], color: '#22c55e', light: '#dcfce7' },
  Z: { cells: [[0,0],[1,0],[1,1],[2,1]], color: '#ef4444', light: '#fee2e2' },
  J: { cells: [[0,0],[0,1],[1,1],[2,1]], color: '#3b82f6', light: '#dbeafe' },
  L: { cells: [[2,0],[0,1],[1,1],[2,1]], color: '#f97316', light: '#ffedd5' },
};
const PIECE_KEYS = Object.keys(PIECES);

function emptyBoard() {
  return Array.from({ length: ROWS }, () => Array(COLS).fill(EMPTY));
}

function rotate90(cells) {
  const maxX = Math.max(...cells.map(c => c[0]));
  return cells.map(([x, y]) => [y, maxX - x]);
}

const LEVEL_COLORS = ['#7c5cfc','#22c55e','#06b6d4','#f59e0b','#ef4444','#a855f7','#f97316','#3b82f6','#ec4899','#10b981'];

export default {
  name: 'TetrisGame1',
  data() {
    return {
      board: emptyBoard(),
      current: null,
      currentX: 0,
      currentY: 0,
      next: null,
      score: 0,
      best: 0,
      lines: 0,
      level: 1,
      running: false,
      over: false,
      loop: null,
    };
  },
  computed: {
    levelColor() {
      return LEVEL_COLORS[(this.level - 1) % LEVEL_COLORS.length] + '22';
    },
    displayBoard() {
      const b = this.board.map(row => [...row]);
      if (this.current) {
        this.current.cells.forEach(([cx, cy]) => {
          const bx = cx + this.currentX;
          const by = cy + this.currentY;
          if (by >= 0 && by < ROWS && bx >= 0 && bx < COLS) {
            b[by][bx] = this.current.color;
          }
        });
      }
      return b;
    },
    nextPreview() {
      const grid = Array.from({ length: 4 }, () => Array(4).fill(EMPTY));
      if (this.next) {
        this.next.cells.forEach(([x, y]) => {
          if (y < 4 && x < 4) grid[y][x] = this.next.color;
        });
      }
      return grid;
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKey);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKey);
    if (this.loop) clearInterval(this.loop);
  },
  methods: {
    cellStyle(color) {
      if (!color) return {
        background: '#f8fafc',
        border: '1px solid #f1f5f9',
      };
      return {
        background: `linear-gradient(135deg, ${color}ee, ${color})`,
        border: `1px solid ${color}`,
        boxShadow: `inset 2px 2px 4px rgba(255,255,255,0.5), inset -1px -1px 3px rgba(0,0,0,0.15), 0 1px 3px ${color}66`,
        borderRadius: '3px',
      };
    },
    newPiece() {
      const key = PIECE_KEYS[Math.floor(Math.random() * PIECE_KEYS.length)];
      const p = PIECES[key];
      return { cells: p.cells.map(c => [...c]), color: p.color };
    },
    startGame() {
      this.board = emptyBoard();
      this.score = 0; this.lines = 0; this.level = 1;
      this.over = false; this.running = true;
      this.next = this.newPiece();
      this.spawnPiece();
      if (this.loop) clearInterval(this.loop);
      this.loop = setInterval(this.tick, this.dropSpeed());
    },
    dropSpeed() { return Math.max(80, 600 - (this.level - 1) * 50); },
    spawnPiece() {
      this.current = this.next;
      this.next = this.newPiece();
      this.currentX = Math.floor(COLS / 2) - 1;
      this.currentY = 0;
      if (!this.canPlace(this.current.cells, this.currentX, this.currentY)) {
        this.running = false; this.over = true;
        clearInterval(this.loop);
        if (this.score > this.best) this.best = this.score;
        this.$emit('game-over', this.score);
      }
    },
    canPlace(cells, ox, oy) {
      return cells.every(([cx, cy]) => {
        const x = cx + ox, y = cy + oy;
        return x >= 0 && x < COLS && y >= 0 && y < ROWS && !this.board[y][x];
      });
    },
    tick() {
      if (this.canPlace(this.current.cells, this.currentX, this.currentY + 1)) {
        this.currentY++;
      } else {
        this.lock();
      }
    },
    lock() {
      this.current.cells.forEach(([cx, cy]) => {
        const x = cx + this.currentX, y = cy + this.currentY;
        if (y >= 0) this.board[y][x] = this.current.color;
      });
      this.clearLines();
      this.spawnPiece();
    },
    clearLines() {
      let cleared = 0;
      for (let r = ROWS - 1; r >= 0; r--) {
        if (this.board[r].every(c => c !== EMPTY)) {
          this.board.splice(r, 1);
          this.board.unshift(Array(COLS).fill(EMPTY));
          cleared++; r++;
        }
      }
      if (cleared) {
        const pts = [0, 100, 300, 500, 800][cleared] * this.level;
        this.score += pts;
        if (this.score > this.best) this.best = this.score;
        this.lines += cleared;
        this.level = Math.floor(this.lines / 10) + 1;
        this.$emit('score-update', this.score);
        clearInterval(this.loop);
        this.loop = setInterval(this.tick, this.dropSpeed());
      }
    },
    moveLeft() {
      if (this.running && this.canPlace(this.current.cells, this.currentX - 1, this.currentY))
        this.currentX--;
    },
    moveRight() {
      if (this.running && this.canPlace(this.current.cells, this.currentX + 1, this.currentY))
        this.currentX++;
    },
    softDrop() {
      if (!this.running) return;
      if (this.canPlace(this.current.cells, this.currentX, this.currentY + 1)) {
        this.currentY++; this.score++; this.$emit('score-update', this.score);
      } else { this.lock(); }
    },
    hardDrop() {
      if (!this.running) return;
      while (this.canPlace(this.current.cells, this.currentX, this.currentY + 1)) {
        this.currentY++; this.score++;
      }
      this.$emit('score-update', this.score);
      this.lock();
    },
    rotate() {
      if (!this.running) return;
      const rotated = rotate90(this.current.cells);
      const ox = this.currentX;
      if (this.canPlace(rotated, ox, this.currentY)) {
        this.current = { ...this.current, cells: rotated };
      } else if (this.canPlace(rotated, ox - 1, this.currentY)) {
        this.current = { ...this.current, cells: rotated }; this.currentX--;
      } else if (this.canPlace(rotated, ox + 1, this.currentY)) {
        this.current = { ...this.current, cells: rotated }; this.currentX++;
      }
    },
    onKey(e) {
      if (!this.running) return;
      const handlers = {
        ArrowLeft: this.moveLeft, ArrowRight: this.moveRight,
        ArrowDown: this.softDrop, ArrowUp: this.hardDrop,
        ' ': this.rotate,
      };
      if (handlers[e.key]) { e.preventDefault(); handlers[e.key](); }
    },
  },
};
</script>

<style scoped>
.tg-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 10px;
  overflow: hidden;
  min-height: 480px;
}

.tg-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: linear-gradient(135deg, #faf5ff, #f3e8ff);
  border-bottom: 1px solid #e9d5ff;
  flex-shrink: 0;
}
.tg-top-pill { display: flex; align-items: center; gap: 6px; }
.tg-top-icon { font-size: 1.1rem; }
.tg-top-title { font-size: .95rem; font-weight: 800; color: #7c3aed; }
.tg-top-stats { display: flex; align-items: center; gap: 10px; }
.tg-top-stat { display: flex; flex-direction: column; align-items: center; }
.tg-top-val { font-size: 1rem; font-weight: 800; color: #7c3aed; }
.tg-top-lbl { font-size: .6rem; color: #c4b5fd; font-weight: 600; text-transform: uppercase; }
.tg-level-pill { background: #ede9fe; border-radius: 8px; padding: 3px 10px; }
.tg-lv-val { font-size: .8rem; color: #7c3aed; }

.tg-main {
  flex: 1;
  display: flex;
  gap: 0;
  min-height: 0;
  padding: 10px;
  justify-content: center;
  background: #fafafa;
}

.tg-board {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: #f1f5f9;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 4px;
  width: calc(10 * 26px + 8px + 4px);
  flex-shrink: 0;
  box-shadow: 0 4px 20px rgba(124,92,252,0.08);
}
.tg-row { display: flex; gap: 1px; }
.tg-cell {
  width: 26px;
  height: 26px;
  border-radius: 3px;
  flex-shrink: 0;
  transition: background 0.05s;
}

.tg-side {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0 4px 12px;
  min-width: 80px;
}
.tg-next-box { }
.tg-side-label {
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .12em;
  color: #94a3b8;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.tg-next {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px;
}
.tg-next-row { gap: 1px; }
.tg-next-cell { width: 16px; height: 16px; border-radius: 2px; }

.tg-side-stat { }
.tg-stat-lbl { font-size: 9px; letter-spacing: .08em; color: #94a3b8; text-transform: uppercase; font-weight: 700; }
.tg-stat-val { font-size: 1.1rem; font-weight: 800; color: #1e293b; }

.tg-overlay {
  position: absolute;
  inset: 0;
  background: rgba(248,250,252,0.94);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}
.tg-ov-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1.5px solid #e9d5ff;
  border-radius: 18px;
  padding: 24px 32px;
  box-shadow: 0 8px 40px rgba(124,92,252,0.14);
}
.tg-ov-emoji { font-size: 2.8rem; }
.tg-ov-title { font-size: 1.3rem; font-weight: 800; color: #7c3aed; margin: 0; }
.tg-ov-sub { font-size: .72rem; color: #c4b5fd; margin: 0; }
.tg-final-score { font-size: 2.4rem; font-weight: 900; color: #7c5cfc; line-height: 1; }
.tg-final-score span { font-size: .9rem; color: #c4b5fd; font-weight: 600; }
.tg-btn {
  margin-top: 6px;
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 28px;
  font-size: .9rem;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(124,92,252,0.35);
  transition: transform .15s, box-shadow .15s;
}
.tg-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(124,92,252,0.45); }

.tg-controls {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  background: linear-gradient(180deg, #f9fafb, #f3f4f6);
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.tg-ctrl-btn {
  width: 48px; height: 40px;
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  color: #374151;
  font-size: .9rem;
  cursor: pointer;
  transition: all .1s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.06);
}
.tg-ctrl-btn:hover { background: #f5f3ff; border-color: #a855f7; color: #7c3aed; }
.tg-ctrl-btn:active { transform: scale(0.93); background: #ede9fe; }
.tg-ctrl-drop {
  width: auto;
  padding: 0 14px;
  background: linear-gradient(135deg, #f3e8ff, #ede9fe);
  border-color: #c4b5fd;
  color: #7c3aed;
  font-weight: 700;
}
</style>
